/**
 * Grupos de interés.
 *
 * La población encuestada. Cada grupo asociado a un instrumento genera un
 * formulario de Google independiente, así que asociar o desasociar cambia
 * cuántos formularios se publicarán. Documental y cuantitativa no los admiten.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type GrupoInteres from '@/interfaces/GrupoInteres'
import { getGruposInteresService, getGrupoInteresByIdService, crearGrupoInteresService, actualizarGrupoInteresService, eliminarGrupoInteresService, asignarGruposAInstrumentoService, desvincularGrupoDeInstrumentoService} from '@/services/GrupoInteresService'

/** Store `GestionGrupoInteres`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionGrupoInteres = defineStore('GestionGrupoInteres', {
  state: () => ({
    grupos: [] as GrupoInteres[],
    grupoActual: null as GrupoInteres | null,
    urlGrupos: `${process.env.VUE_APP_API_BASE_URL}/api/grupos-interes`
  }),

  actions: {
    async getGrupos() {
      const data = await getGruposInteresService(this.urlGrupos)
      this.grupos = data
      return data
    },

    async getGrupoById(id: number) {
      const data = await getGrupoInteresByIdService(this.urlGrupos, id)
      if (data) this.grupoActual = data
      return data
    },

    async crearGrupo(payload: Omit<GrupoInteres, 'id_grupo_interes'>) {
      const nuevo = await crearGrupoInteresService(this.urlGrupos, payload)
      if (nuevo) {
        this.grupos.push(nuevo)
        this.grupoActual = nuevo
      }
      return nuevo
    },

    async actualizarGrupo(id: number, payload: Partial<GrupoInteres>) {
      const actualizado = await actualizarGrupoInteresService(this.urlGrupos, id, payload)
      if (actualizado) {
        const idx = this.grupos.findIndex(g => g.id_grupo_interes === id)
        if (idx !== -1) this.grupos[idx] = actualizado
        if (this.grupoActual?.id_grupo_interes === id) {
          this.grupoActual = actualizado
        }
      }
      return actualizado
    },

    async eliminarGrupo(id: number) {
      const ok = await eliminarGrupoInteresService(this.urlGrupos, id)
      if (ok) {
        this.grupos = this.grupos.filter(g => g.id_grupo_interes !== id)
        if (this.grupoActual?.id_grupo_interes === id) {
          this.grupoActual = null
        }
      }
      return ok
    },

    async asignarGruposAInstrumento(idInstrumento: number, grupos: number[]) {
      return await asignarGruposAInstrumentoService(idInstrumento, grupos)
    },

    async desvincularGrupoDeInstrumento(idInstrumento: number, idGrupo: number) {
      return await desvincularGrupoDeInstrumentoService(idInstrumento, idGrupo)
    }
  }
})